import { create } from 'zustand';

import { vhAdmGetCurrentUser } from '@/admin/admin.api';

// The live API returns "super-admin" (hyphen) even though the swagger docs say
// "super_admin" (underscore) — accept both so a docs fix upstream doesn't lock everyone out.
const SYSTEM_ADMIN_ROLES = new Set(['admin', 'super_admin', 'super-admin']);

/** Only a super admin may grant or remove admin rights (AUTH POST /user/assign-role/:id). */
export const isSuperAdminRole = (role: string | null | undefined) =>
  role === 'super-admin' || role === 'super_admin';

export type VhAdminAccessStatus = 'idle' | 'checking' | 'allowed' | 'denied';

type VhAdminAccessState = {
  status: VhAdminAccessStatus;
  /** The signed-in admin's AUTH id and role, once checked. */
  userId: string | null;
  role: string | null;
  /** Fetches the current user's AUTH role once and caches the verdict — safe to
   *  call from every consumer (guard, header link, ...) without refetching. */
  check: () => Promise<void>;
};

export const useVhAdminAccessStore = create<VhAdminAccessState>((set, get) => ({
  status: 'idle',
  userId: null,
  role: null,
  check: async () => {
    if (get().status !== 'idle') return;

    set({ status: 'checking' });
    try {
      const user = await vhAdmGetCurrentUser();
      set({
        status: SYSTEM_ADMIN_ROLES.has(user.role) ? 'allowed' : 'denied',
        userId: user.id,
        role: user.role,
      });
    } catch {
      set({ status: 'denied' });
    }
  },
}));
